/**
 * A aba de busca, portada de app.js:607-681.
 *
 * A diferença deliberada com o painel atual: o termo procurado vive na URL
 * (`?q=`). Lá a busca não entrava na barra de endereços, então o resultado não
 * sobrevivia a um F5 nem cabia num link de code review. Sem `q`, a aba mostra o
 * histórico de buscas — que era o `showSearchHistory`.
 */
import type { MouseEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { postar } from '../api/cliente';
import { chaves, useBusca, useBuscasRecentes, useContagemDeNotas } from '../api/consultas';
import type { Achado, BuscaRecente, RespostaDeBusca } from '../api/tipos';
import { useSessao } from '../auth/sessao';
import { acionavel } from '../componentes/acionavel';
import { Carregando, Falha, Vazio } from '../componentes/Estado';
import { copiar, useRecado } from '../componentes/Recado';

export function Busca({ slug }: { slug: string }) {
  const [parametros] = useSearchParams();
  const q = (parametros.get('q') || '').trim();
  const navegar = useNavigate();
  const avisar = useRecado();
  const fila = useQueryClient();
  const { admin } = useSessao();
  const busca = useBusca(slug, q);
  const notas = useContagemDeNotas(slug);

  const reindexar = useMutation({
    mutationFn: () => postar<{ error?: string | null }>(`/api/${slug}/reindex`),
    onSuccess: async (r) => {
      avisar(r?.error ? `reindex falhou: ${r.error}` : 'índice regerado');
      await fila.invalidateQueries({ queryKey: chaves.busca(slug, q) });
    },
    onError: (e) => avisar(`reindex falhou: ${e instanceof Error ? e.message : String(e)}`),
  });

  if (!q) return <HistoricoDeBuscas slug={slug} />;
  if (reindexar.isPending) {
    return <Carregando rotulo="reindexando o acervo — isso chama o modelo de embeddings…" />;
  }
  if (busca.isPending) return <Carregando rotulo={`a busca por “${q}”`} />;
  if (busca.error) {
    return <Falha erro={busca.error} rotulo={`a busca por “${q}”`} aoRepetir={busca.refetch} />;
  }

  const r: RespostaDeBusca = busca.data ?? {};
  const achados = r.results || [];
  const banner = (
    <BannerDoIndice r={r} admin={admin} aoIndexar={() => reindexar.mutate()} />
  );

  if (!achados.length) {
    // o quanto faltou vira evidência na tela: sem isto a pessoa acha que a
    // busca quebrou, quando o que houve foi ausência de documentação
    const perto = r.weak && r.best_score != null && (
      <>
        {' '}A doc mais próxima ficou em <b>{r.best_score}</b> de similaridade, abaixo do piso de{' '}
        <b>{r.min_score}</b> — então não é falha da busca, é assunto que ninguém documentou.
      </>
    );
    return (
      <>
        {banner}
        <div className="sec-title">Nenhum resultado</div>
        <Vazio
          titulo={`“${q}” não casou com nada neste projeto`}
          acao={{ rotulo: 'Ver lacunas abertas', ao: () => navegar(`/p/${slug}/health`) }}
          comando={`eng search "${q.replace(/"/g, '')}" --all`}
        >
          Ou o time nunca documentou isso, ou está escrito com outras palavras. Vale tentar o termo
          que aparece no código (nome de classe, rota, arquivo). Se realmente não existe, registrar
          a lacuna evita que a próxima pessoa gaste o mesmo tempo — o agente faz isso com{' '}
          <code>report_gap</code>.{perto}
        </Vazio>
        <FaixaDeBuscasRecentes slug={slug} />
      </>
    );
  }

  const maior = Math.max(...achados.map((a) => a.score), 0.001);

  return (
    <>
      {banner}
      <div className="sec-title">
        Resultados{' '}
        {r.cached
          ? <span className="cache-badge">⚡ do cache (sem reprocessar LLM)</span>
          : (
            <span
              className="cache-badge"
              style={{ background: 'var(--brand-wash)', color: 'var(--brand-deep)' }}
            >
              novo · LLM
            </span>
          )}
      </div>
      <div className="sec-sub">
        “{q}” · {r.fallback === 'graph' ? 'busca léxica (fallback)' : 'busca semântica local'}
      </div>
      {achados.map((a) => (
        <Resultado
          key={a.unit_key}
          achado={a}
          maior={maior}
          notas={notas.data?.[a.unit_key] || 0}
          aoAbrir={() => navegar(`/p/${slug}/doc?key=${encodeURIComponent(a.unit_key)}`)}
        />
      ))}
      <FaixaDeBuscasRecentes slug={slug} />
    </>
  );
}

/**
 * O motivo do resultado ruim é banner, nunca tela de falha: o fallback léxico
 * devolveu hits e eles continuam valendo. Trocá-los por "não deu para carregar"
 * esconderia resultado que existe (app.js:615).
 */
function BannerDoIndice({ r, admin, aoIndexar }: {
  r: RespostaDeBusca;
  admin: boolean;
  aoIndexar: () => void;
}) {
  if (r.needsIndex) {
    return (
      <div className="banner-bad">
        <div>
          <b>Índice semântico não gerado neste projeto.</b>
          <div className="muted" style={{ marginTop: 4 }}>
            Sem embeddings, isto aqui é busca léxica no grafo e no texto — recall pior, sem
            sinônimo.{admin ? '' : ' Peça a um admin para rodar o reindex.'}
          </div>
        </div>
        {admin && <button type="button" className="btn" onClick={aoIndexar}>Indexar agora</button>}
      </div>
    );
  }
  if (r.error) {
    return (
      <div className="banner-bad">
        <div>
          <b>A busca semântica falhou; caiu no fallback léxico.</b>
          <div className="muted" style={{ marginTop: 4 }}>{r.error}</div>
        </div>
      </div>
    );
  }
  return null;
}

function Resultado({ achado, maior, notas, aoAbrir }: {
  achado: Achado;
  maior: number;
  notas: number;
  aoAbrir: () => void;
}) {
  const avisar = useRecado();
  const trecho = (achado.body_md || '').replace(/[#*`]/g, '').slice(0, 200);
  const citacao = `${achado.unit_key} @${(achado.source_sha || '').slice(0, 8)}\n${trecho}`;

  // copiar não é abrir: o cartão inteiro leva à doc, então sem parar a
  // propagação quem copia o comando perde a lista de resultados (app.js:652)
  const copiarSemAbrir = async (e: MouseEvent<HTMLButtonElement>, texto: string) => {
    e.stopPropagation();
    avisar(await copiar(texto) ? 'copiado' : 'não consegui copiar — selecione o texto e use Ctrl+C');
  };

  return (
    <div className="result" onClick={aoAbrir}>
      <div className="rk">
        <span className="path">{achado.title || achado.unit_key}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="scorebar">
            <i style={{ width: `${((achado.score / maior) * 100).toFixed(0)}%` }} />
          </span>
          <span className="score">{achado.score}</span>
        </span>
      </div>
      <div className="snip">{trecho}…</div>
      <div className="result-acts">
        <button type="button" onClick={aoAbrir}>abrir doc</button>
        <button type="button" onClick={(e) => void copiarSemAbrir(e, `eng doc ${achado.unit_key}`)}>
          copiar eng doc
        </button>
        <button type="button" onClick={(e) => void copiarSemAbrir(e, citacao)}>
          copiar trecho + SHA
        </button>
        {notas > 0 && (
          <button type="button" onClick={aoAbrir}>{notas} notas do time</button>
        )}
      </div>
    </div>
  );
}

/**
 * A faixa embaixo do resultado cala quando não dá para ler o histórico: ela é
 * apoio, e um banner vermelho aqui roubaria a tela do que se veio ler. Quem
 * pede o histórico como conteúdo principal vê a falha inteira, logo abaixo.
 */
function FaixaDeBuscasRecentes({ slug }: { slug: string }) {
  const recentes = useBuscasRecentes(slug);
  const lista = recentes.data || [];
  if (!lista.length) return null;
  return <ListaDeBuscas slug={slug} lista={lista} />;
}

function ListaDeBuscas({ slug, lista }: { slug: string; lista: BuscaRecente[] }) {
  const navegar = useNavigate();
  const reusar = (termo: string) => navegar(`/p/${slug}/search?q=${encodeURIComponent(termo)}`);
  return (
    <>
      <div className="sec-title" style={{ marginTop: 30, fontSize: 16 }}>
        Buscas recentes{' '}
        <span className="muted" style={{ fontWeight: 400 }}>· clique para reusar sem reprocessar</span>
      </div>
      <div className="sh-list">
        {lista.map((h) => (
          <div key={h.query} className="sh-item" title={h.query} {...acionavel(() => reusar(h.query))}>
            <span className="sh-q">{h.query}</span>
            <span className="sh-tags">
              <span>{h.count} resultados</span>
              <span>{h.by || '—'}</span>
            </span>
            <span className="sh-hits">{h.hits}× reusada</span>
          </div>
        ))}
      </div>
    </>
  );
}

function HistoricoDeBuscas({ slug }: { slug: string }) {
  const recentes = useBuscasRecentes(slug);

  if (recentes.isPending) return <Carregando rotulo="o histórico de buscas" />;
  if (recentes.error) {
    return <Falha erro={recentes.error} rotulo="o histórico de buscas" aoRepetir={recentes.refetch} />;
  }

  const lista = recentes.data || [];
  return (
    <>
      <div className="sec-title">Histórico de buscas</div>
      <div className="sec-sub">Buscas já feitas ficam salvas — reusá-las não gasta LLM.</div>
      {lista.length
        ? <ListaDeBuscas slug={slug} lista={lista} />
        : (
          <Vazio titulo="Ninguém buscou nada ainda neste projeto">
            Cada pergunta feita aqui ou pelo agente fica registrada com quantos resultados trouxe.
            Isso mostra o que o time procura e o que não encontra.
          </Vazio>
        )}
    </>
  );
}
